/**
 * @file GlobalSummaryTab.js
 * @description Represents a tab component for displaying global summary data.
 * @module GlobalSummaryTab
 */

/**
 * Represents the GlobalSummaryTab component.
 * @param {Object} props - The component props.
 * @param {Array} props.data - The data for computing the global summary.
 * @param {boolean} props.isSSSelected - Indicates if a SSO is selected.
 * @returns {JSX.Element} The rendered GlobalSummaryTab component.
 */
import React, { useState, useEffect } from "react";
import NotFoundBox from "../../ui/no_data_indicators/NotFoundBox";
import Spinner from "../../ui/spinners/Spinner";
import { computeGlobalSummary } from "../../../services/ssoSummaryService";
import styles from "../sso.module.scss";
import { getText } from '../../../i18n';

const GlobalSummaryTab = ({ data, isSSSelected }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);

  useEffect(() => {
    if (isSSSelected) {
      loadSummaryData();
    }
  }, [isSSSelected, data]);

  const loadSummaryData = async () => {
    setIsLoading(true);
    try {
      const summary = await computeGlobalSummary(data);
      setSummaryData(summary);
    } catch (error) {
      console.error("Error loading summary data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSSSelected) {
    return (
      <NotFoundBox
        backgroundColor="#f0f4ff"
        title={getText('msg_7cc6a6e8c21a')}
        description={getText('msg_79c9e9f96862')}
      />
    );
  }

  if (isLoading) {
    return <Spinner width={50} className={styles.loadingSpinner} />;
  }

  return (
    <div className="col-12 m-auto pb-5">
      <div className="table-responsive">
        <table className="table table-hover align-middle ing-table-striped">
          <thead className="custom-table-header table-light">
            <tr>
              <th>{getText('msg_95998fa9bfb8')}</th>
              <th>{getText('msg_2d0ad15e4439')}</th>
              <th>{getText('msg_13f283d69156')}</th>
              <th>{getText('msg_bae7d5be7082')}</th>
              <th>{getText('msg_a086d942884a')}</th>
              <th>{getText('msg_8dce170de238')}</th>
              <th>{getText('msg_55f8ebc805e6')}</th>
              <th>{getText('msg_83359eb8b8f0')}</th>
              <th>{getText('msg_2e64fbc61618')}</th>
              <th>{getText('msg_67bbb2862edb')}</th>
            </tr>
          </thead>
          <tbody>
            {summaryData.map((row, index) => (
              <tr key={index}>
                {Object.values(row).map((value, cellIndex) => (
                  <td key={cellIndex}>{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GlobalSummaryTab;
